<!-- eslint-disable max-lines -->
<template>
	<div>
		<QuestionCard title="Which SSO provider are you connecting to?">
			<validation-provider
				#default="{ errors }"
				name="SSO Provider"
				:rules="{ required: !ssoConfiguration.ssoProviderId }"
				:value="ssoConfiguration.ssoProviderId"
			>
				<v-row dense>
					<v-col cols="12">
						<div class="d-flex flex-wrap">
							<SelectorCard
								v-for="provider in ssoProviders"
								:key="provider.id"
								:title="provider.ssoProviderName"
								:model-value="ssoConfiguration.ssoProviderId === provider.id"
								:disabled="!userFullPermissions"
								:data-test-id="`sso-provider-${provider.id}`"
								class="mr-4 mb-4"
								@input="onSelectProvider(provider.id)"
							>
								<template #icon>
									<img
										v-if="provider.id !== OTHER_PROVIDER"
										:src="getProviderImage(provider.id)"
										:alt="provider.ssoProviderName"
										width="36"
									>
									<v-icon
										v-else
										color="primary"
										size="36"
									>
										mdi-cog
									</v-icon>
								</template>
							</SelectorCard>
						</div>
						<div
							v-if="errors[0]"
							class="error--text text-caption mt-2 v-messages__message"
							role="alert"
							aria-live="polite"
							data-testid="sso-provider-required-error"
						>
							{{ errors[0] }}
						</div>
					</v-col>
				</v-row>
			</validation-provider>
		</QuestionCard>

		<QuestionCard
			v-if="ssoConfiguration.ssoProviderId === OTHER_PROVIDER"
			title="What is the name of your SSO provider?"
		>
			<v-row dense>
				<v-col
					cols="12"
					md="8"
					lg="6"
				>
					<TextField
						:value.sync="ssoConfiguration.name"
						:label="`SSO Provider Name${ssoConfiguration.isEnabled ? ' *' : ''}`"
						:rules="ssoConfiguration.isEnabled ? 'required' : ''"
						test-id="sso-configuration-name"
						:disabled="!userFullPermissions"
						@input="updateSsoConfiguration('name', $event)"
					/>
				</v-col>
			</v-row>
		</QuestionCard>

		<QuestionCard
			title="Enter your Configuration GUID (or click Generate)"
			description="This is a unique identifier (GUID) assigned to each Cassie SSO configuration. This helps Cassie distinguish one SSO configuration from another and must be unique."
		>
			<v-row dense>
				<v-col
					cols="12"
					md="8"
					lg="6"
				>
					<TextField
						:value.sync="ssoConfiguration.tenantGuid"
						label="Configuration GUID"
						:rules="{required: true, noUppercase: true}"
						test-id="sso-configuration-organization-id"
						readonly
					/>
				</v-col>
				<v-col
					cols="12"
					md="4"
					lg="3"
				>
					<PrimaryActionButton
						v-if="userFullPermissions"
						small-width
						@click="generateGuid"
					>
						Generate
					</PrimaryActionButton>
				</v-col>
			</v-row>
		</QuestionCard>

		<!-- SCIM Configuration -->
		<QuestionCard
			title="Would you like to enable SCIM to automate User management?"
			description="SCIM (System for Cross-domain Identity Management) enables users to be created automatically and deactivated automatically when changes occur in your Identity Provider."
			:show-yes-no-buttons="userFullPermissions"
			:value="ssoConfiguration.scimEnabled"
			@input="handleScimToggle"
		>
			<!-- Show info alert when SCIM cannot be enabled (but can still be disabled) -->
			<v-alert
				v-if="cannotEnableScim && !ssoConfiguration.scimEnabled"
				type="info"
				dense
				outlined
				class="mt-2 mb-0"
			>
				SSO must be enabled first to enable SCIM.
			</v-alert>
			<template v-if="ssoConfiguration.scimEnabled">
				<!-- SCIM URI (readonly) -->
				<v-row
					dense
					class="mt-4"
				>
					<v-col
						cols="12"
						md="8"
						lg="6"
					>
						<TextField
							:value="scimUri"
							label="SCIM Endpoint URI"
							readonly
							data-test-id="scim-endpoint-uri"
							explainer="Configure this URI in your Identity Provider as the SCIM endpoint"
						>
							<template #append>
								<v-btn
									icon
									small
									@click="copyToClipboard(scimUri)"
								>
									<v-icon small>
										mdi-content-copy
									</v-icon>
								</v-btn>
							</template>
						</TextField>
					</v-col>
				</v-row>

				<!-- Token Section -->
				<div class="scim-token-section mt-6">
					<div
						v-if="!ssoConfiguration.scimToken && !ssoConfiguration.pendingScimToken"
						class="text-subtitle-1 font-weight-medium mb-3"
					>
						Bearer Token
					</div>

					<!-- State 1: Newly generated token (show once) -->
					<div
						v-if="newlyGeneratedToken"
						class="generated-token-display"
					>
						<v-alert
							type="warning"
							outlined
							dense
							class="mb-3"
						>
							Copy this token somewhere safe. You won't be able to view it again after leaving this page.
						</v-alert>
						<v-row dense>
							<v-col
								cols="12"
								md="8"
								lg="6"
							>
								<TextField
									:value="newlyGeneratedToken"
									label="Bearer Token"
									readonly
									data-test-id="scim-generated-token"
								>
									<template #append>
										<v-btn
											icon
											small
											@click="copyToClipboard(newlyGeneratedToken)"
										>
											<v-icon small>
												mdi-content-copy
											</v-icon>
										</v-btn>
									</template>
								</TextField>
							</v-col>
							<v-col
								cols="12"
								md="4"
								lg="3"
							>
								<PrimaryActionButton
									small-width
									@click="dismissGeneratedToken"
								>
									Done
								</PrimaryActionButton>
							</v-col>
						</v-row>
					</div>

					<!-- State 2: Existing saved token (show metadata only) -->
					<div
						v-else-if="ssoConfiguration.scimToken"
						class="existing-token-display"
					>
						<v-card
							flat
							class="pa-4 cassie-card-background"
						>
							<v-card-title class="pa-0 pb-3 text-subtitle-1 font-weight-medium">
								Bearer Token
							</v-card-title>
							<div class="d-flex flex-column">
								<div class="d-flex mb-2">
									<span class="text-body-2 grey--text mr-2">
										Description:
									</span>
									<span class="text-body-2">
										{{ ssoConfiguration.scimToken.description || 'No description' }}
									</span>
								</div>
								<div class="d-flex mb-2">
									<span class="text-body-2 grey--text mr-2">
										Created:
									</span>
									<span class="text-body-2">
										{{ formatDate(ssoConfiguration.scimToken.dateCreated) }}
									</span>
								</div>
								<div class="d-flex mb-2">
									<span class="text-body-2 grey--text mr-2">
										Expires:
									</span>
									<span
										class="text-body-2"
										:class="{ 'error--text': ssoConfiguration.scimToken.isExpired }"
									>
										{{ ssoConfiguration.scimToken.expiresAt ? formatDate(ssoConfiguration.scimToken.expiresAt) : 'Never' }}
										<span
											v-if="ssoConfiguration.scimToken.isExpired"
											class="ml-1"
										>
											(Expired)
										</span>
									</span>
								</div>
							</div>
							<div class="d-flex mt-4">
								<SecondaryActionButton
									class="mr-2"
									:disabled="!userFullPermissions"
									@click="showRegenerateConfirm = true"
								>
									Regenerate Token
								</SecondaryActionButton>
								<DeleteButton
									outlined
									:disabled="!userFullPermissions"
									@click="showRevokeConfirm = true"
								>
									Revoke Token
								</DeleteButton>
							</div>
						</v-card>
					</div>

					<!-- State 3: Pending token (not yet saved to server) -->
					<div
						v-else-if="ssoConfiguration.pendingScimToken"
						class="pending-token-display"
					>
						<v-alert
							type="info"
							dense
							outlined
							class="mb-3"
						>
							Token will be saved when you click Save Changes or Create.
						</v-alert>
						<v-card
							flat
							outlined
							class="pa-4 cassie-card-background"
						>
							<v-card-title class="pa-0 pb-3 text-subtitle-1 font-weight-medium">
								Bearer Token
							</v-card-title>
							<div class="d-flex flex-column">
								<div class="d-flex mb-2">
									<span class="text-body-2 grey--text mr-2">
										Description:
									</span>
									<span class="text-body-2">
										{{ ssoConfiguration.pendingScimToken.description || 'No description' }}
									</span>
								</div>
								<div class="d-flex mb-2">
									<span class="text-body-2 grey--text mr-2">
										Expires:
									</span>
									<span class="text-body-2">
										{{ ssoConfiguration.pendingScimToken.expiresAt ? formatDate(ssoConfiguration.pendingScimToken.expiresAt) : 'Never' }}
									</span>
								</div>
							</div>
							<div class="d-flex mt-4">
								<SecondaryActionButton
									class="mr-2"
									:disabled="!userFullPermissions"
									@click="showRegenerateConfirm = true"
								>
									Regenerate Token
								</SecondaryActionButton>
								<DeleteButton
									outlined
									:disabled="!userFullPermissions"
									@click="clearPendingToken"
								>
									Clear Token
								</DeleteButton>
							</div>
						</v-card>
					</div>

					<!-- State 4: No token exists -->
					<div
						v-else
						class="no-token"
					>
						<p class="text-body-2 grey--text mb-3">
							No bearer token has been generated yet.
						</p>
						<SecondaryActionButton
							:disabled="!userFullPermissions"
							@click="showGenerateForm = true"
						>
							Generate Token
						</SecondaryActionButton>
					</div>

					<!-- Generate Token Form (modal) -->
					<Modal
						v-if="showGenerateForm"
						max-width="500"
					>
						<template #modal-title>
							Generate SCIM Bearer Token
						</template>
						<template #modal-content>
							<div class="cassie-vertical-md">
								<TextField
									v-model="newTokenDescription"
									label="Description (optional)"
									placeholder="e.g., Entra ID Production"
									data-test-id="scim-token-description"
								/>
								<DatePicker
									:date.sync="newTokenExpiry"
									:display-date="newTokenExpiryFormatted"
									:min="tomorrow"
									label="Token Expiry (optional)"
									class="mt-4"
								/>
							</div>
						</template>
						<template #modal-footer>
							<SecondaryActionButton
								@click="showGenerateForm = false"
							>
								Cancel
							</SecondaryActionButton>
							<PrimaryActionButton
								:disabled="!userFullPermissions"
								@click="generateToken"
							>
								Generate
							</PrimaryActionButton>
						</template>
					</Modal>
				</div>
			</template>
		</QuestionCard>

		<!-- Regenerate Confirmation Modal -->
		<Modal
			v-if="showRegenerateConfirm"
			max-width="450"
		>
			<template #modal-title>
				Regenerate Token?
			</template>
			<template #modal-content>
				This will invalidate the existing token. Any Identity Provider using the current token will lose access until reconfigured with the new token.
			</template>
			<template #modal-footer>
				<SecondaryActionButton
					@click="showRegenerateConfirm = false"
				>
					Cancel
				</SecondaryActionButton>
				<PrimaryActionButton
					@click="regenerateToken"
				>
					Regenerate
				</PrimaryActionButton>
			</template>
		</Modal>

		<!-- Revoke Confirmation Modal -->
		<Modal
			v-if="showRevokeConfirm"
			max-width="450"
		>
			<template #modal-title>
				<span class="error--text">
					Revoke Token?
				</span>
			</template>
			<template #modal-content>
				This will immediately revoke the token. Any Identity Provider using this token will lose access.
			</template>
			<template #modal-footer>
				<SecondaryActionButton
					@click="showRevokeConfirm = false"
				>
					Cancel
				</SecondaryActionButton>
				<DeleteButton
					@click="revokeToken"
				>
					Revoke
				</DeleteButton>
			</template>
		</Modal>

		<!-- GUID Change Warning Modal (SCIM Re-provisioning) -->
		<Modal
			v-if="showGuidChangeWarning"
			max-width="500"
		>
			<template #modal-title>
				Change Configuration GUID
			</template>
			<template #modal-content>
				<p class="mb-4">
					Changing your Configuration GUID will require re-provisioning SCIM users and groups.
				</p>
				<p>
					All users provisioned via SCIM under the current GUID will have their SCIM association cleared,
					and all SCIM groups will be archived. Your Identity Provider will need to re-provision users and groups.
				</p>
			</template>
			<template #modal-footer>
				<SecondaryActionButton @click="cancelGuidChange">
					Cancel
				</SecondaryActionButton>
				<PrimaryActionButton @click="confirmGuidChange">
					Change GUID
				</PrimaryActionButton>
			</template>
		</Modal>
	</div>
</template>

<script>
/* eslint-disable max-lines */
import { format } from 'date-fns'
import { QuestionCard, SelectorCard } from '../../../../components/component-library-wrapper.js'
import TextField from '../../../../../../shared/components/text-field.vue'
import { uuid } from '../../../../../../shared/utils/uuid.js'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import DeleteButton from '../../../../../../shared/components/delete-button.vue'
import Modal from '../../../../../../shared/components/modal.vue'
import DatePicker from '../../../../../../shared/components/date-picker.vue'
import { revokeScimToken } from '../../../../../../shared/utils/api/sso-configuration.js'
import { getEnv } from '../../../../../../shared/utils/runtime-settings.js'
import LogoADFS from './sso-logos/ADFS-logo.png'
import LogoOkta from './sso-logos/Okta-logo.png'
import LogoGoogle from './sso-logos/Google-logo.png'
import LogoEntra from './sso-logos/Microsoft-Entra-ID-logo.png'

export default {
	components: {
		QuestionCard,
		TextField,
		SelectorCard,
		PrimaryActionButton,
		SecondaryActionButton,
		DeleteButton,
		Modal,
		DatePicker
	},
	props: {
		ssoConfiguration: Object,
		userFullPermissions: Boolean,
		lookupData: Object,
		isConfigurationComplete: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			OTHER_PROVIDER: 5,
			// SCIM token state
			newlyGeneratedToken: null,
			showGenerateForm: false,
			showRegenerateConfirm: false,
			showRevokeConfirm: false,
			newTokenDescription: '',
			newTokenExpiry: null,
			// GUID change warning state
			showGuidChangeWarning: false,
			pendingGuidChange: null,
			originalTenantGuid: null,
			// Track which config we're editing to detect switches
			currentConfigId: null
		}
	},
	computed: {
		cannotEnableScim () {
			// Cannot enable SCIM when:
			// 1. The configuration is complete (has all required fields to be enabled)
			// 2. AND SSO is currently disabled
			// This allows SCIM to be toggled for new configs and in-progress configs
			return this.isConfigurationComplete && !this.ssoConfiguration.isEnabled
		},
		ssoProviders () {
			return this.lookupData.ssoProviders
		},
		scimUri () {
			let authBase = ''
			try {
				authBase = getEnv('VUE_APP_BASE_URL_AUTH')
			} catch {
				authBase = ''
			}
			const tenantGuid = this.ssoConfiguration?.tenantGuid || '{tenant-guid}'
			return `${authBase}/${tenantGuid}/scim/v2/`
		},
		tomorrow () {
			const date = new Date()
			date.setDate(date.getDate() + 1)
			return date.toISOString().split('T')[0]
		},
		newTokenExpiryFormatted () {
			if (!this.newTokenExpiry) return ''
			return format(new Date(this.newTokenExpiry), 'dd/MM/yyyy')
		}
	},
	watch: {
		// Reset the original tenant GUID whenever a new SSO configuration object is provided
		// Note: Intentionally not using 'deep' option - we only want to detect when the parent
		// replaces the entire object reference, not when individual properties are mutated
		ssoConfiguration: {
			immediate: true,
			handler (newConfig) {
				if (!newConfig) {
					this.originalTenantGuid = null
					this.currentConfigId = null
					return
				}
				// Reset originalTenantGuid only when switching to a different configuration
				// or on initial load. This preserves immutability during an edit session.
				const isInitialLoad = this.currentConfigId === null
				const isDifferentConfig = this.currentConfigId !== newConfig.id
				if (isInitialLoad || isDifferentConfig) {
					this.originalTenantGuid = newConfig.tenantGuid || null
					this.currentConfigId = newConfig.id
				}
			}
		}
	},
	methods: {
		async onSelectProvider (id) {
			if (!this.userFullPermissions) return
			await this.updateSsoConfiguration('ssoProviderId', id)
			if (id === this.OTHER_PROVIDER) {
				await this.updateSsoConfiguration('name', '')
				await this.updateSsoConfiguration('description', '')
			}
		},
		updateSsoConfiguration (property, value) {
			this.$emit('update:ssoConfiguration', {
				...this.ssoConfiguration,
				[property]: value
			})
		},
		handleScimToggle (value) {
			// Block enabling SCIM if SSO is disabled on a complete configuration
			// But always allow disabling SCIM (to fix any invalid states)
			if (value === true && this.cannotEnableScim) {
				return
			}
			this.updateSsoConfiguration('scimEnabled', value)
		},
		generateGuid () {
			const newGuid = uuid()

			// Check if SCIM is enabled and config is complete - show warning about re-provisioning
			if (this.ssoConfiguration.scimEnabled && this.isConfigurationComplete && this.originalTenantGuid) {
				this.pendingGuidChange = newGuid
				this.showGuidChangeWarning = true
			} else {
				this.updateSsoConfiguration('tenantGuid', newGuid)
			}
		},
		confirmGuidChange () {
			// Emit both properties in a single update to avoid race conditions
			// where the second emit could overwrite the first before Vue processes it
			// Note: originalTenantGuid remains immutable throughout the edit session
			// so that previousTenantGuid always points to the server-persisted value,
			// not an intermediate unsaved GUID from multiple Generate clicks
			this.$emit('update:ssoConfiguration', {
				...this.ssoConfiguration,
				previousTenantGuid: this.originalTenantGuid,
				tenantGuid: this.pendingGuidChange
			})
			this.showGuidChangeWarning = false
			this.pendingGuidChange = null
		},
		cancelGuidChange () {
			this.showGuidChangeWarning = false
			this.pendingGuidChange = null
			// GUID remains unchanged
		},
		getProviderImage (id) {
			const provider = this.ssoProviders?.find(p => p.id === id)
			if (!provider) return ''
			const name = (provider.ssoProviderName || '').toLowerCase()
			if (name.includes('okta')) return LogoOkta
			if (name.includes('google')) return LogoGoogle
			if (name.includes('entra') || name.includes('azure')) return LogoEntra
			if (name.includes('adfs')) return LogoADFS
			return ''
		},
		formatDate (dateString) {
			if (!dateString) return ''
			return format(new Date(dateString), 'dd/MM/yyyy HH:mm')
		},
		generateSecureToken () {
			const array = new Uint8Array(48)
			crypto.getRandomValues(array)
			return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
		},
		generateToken () {
			const token = this.generateSecureToken()

			// Store the pending token data - it will be saved when the main config is saved
			const pendingToken = {
				bearerToken: token,
				description: this.newTokenDescription || null,
				expiresAt: this.newTokenExpiry ? new Date(this.newTokenExpiry).toISOString() : null
			}

			// Update the parent's ssoConfiguration with the pending token
			this.updateSsoConfiguration('pendingScimToken', pendingToken)

			// Show the token once to the user
			this.newlyGeneratedToken = token
			this.showGenerateForm = false
			this.newTokenDescription = ''
			this.newTokenExpiry = null
		},
		regenerateToken () {
			this.showRegenerateConfirm = false
			this.showGenerateForm = true
		},
		async revokeToken () {
			// If we have a saved config with a real token, revoke via API
			if (this.ssoConfiguration.id && this.ssoConfiguration.scimToken) {
				try {
					await revokeScimToken(this.ssoConfiguration.id)
					this.showRevokeConfirm = false
					// Clear the token from local state via .sync pattern
					this.updateSsoConfiguration('scimToken', null)
				} catch (error) {
					console.error('Failed to revoke token', error)
					this.$toast?.error?.('Failed to revoke token') || alert('Failed to revoke token')
				}
			} else {
				// Just clear the pending token if config not saved yet
				this.updateSsoConfiguration('pendingScimToken', null)
				this.newlyGeneratedToken = null
				this.showRevokeConfirm = false
			}
		},
		dismissGeneratedToken () {
			this.newlyGeneratedToken = null
		},
		clearPendingToken () {
			this.updateSsoConfiguration('pendingScimToken', null)
			this.newlyGeneratedToken = null
		},
		async copyToClipboard (text) {
			try {
				await navigator.clipboard.writeText(text)
				this.$toast?.success?.('Copied to clipboard') || alert('Copied to clipboard')
			} catch (error) {
				console.error('Failed to copy', error)
				this.$toast?.error?.('Failed to copy to clipboard') || alert('Failed to copy to clipboard')
			}
		}
	}
}
</script>
